<template>
  <div class="flex flex-col">
    <div class="overflow-visible">
      <table class="min-w-full divide-y divide-gray-200 select-none">
        <thead class="bg-gray-50">
          <tr>
            <th class="th-cell">체결시각</th>
            <th class="th-cell">종목명</th>
            <th class="th-cell">만기</th>
            <th class="th-cell w-32">잔존만기</th>
            <th class="th-cell w-24">민평</th>
            <th class="th-cell w-24">체결금리</th>
            <th class="th-cell w-24">스프레드</th>
            <th v-if="isAdmin" class="th-cell w-16">제외</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="sortedTransactions.length === 0">
            <td colspan="8" class="px-4 py-8 text-sm text-center text-gray-500">
              조회된 데이터가 없습니다.
            </td>
          </tr>
          <tr v-for="transaction in sortedTransactions" :key="transaction.id" class="hover:bg-gray-50">
            <td class="td-cell">
              <div class="flex items-center gap-1">
                {{ formatTransactionTime(transaction.transactionDateTime) }}
                <span v-if="getDaysAgo(transaction.transactionDateTime) > 0" class="text-xs text-gray-500">
                  ({{ getDaysAgo(transaction.transactionDateTime) }}일 전)
                </span>
              </div>
            </td>
            <td class="td-cell">
              <span class="truncate text-gray-600">
                {{ transaction.originalBondName }}
              </span>
            </td>
            <td class="td-cell">{{ transaction.maturityDate }}</td>
            <td class="td-cell text-gray-500">
              {{ formatRemainingMaturity(transaction.maturityDate) }}
            </td>
            <td class="td-cell">{{ formatYield(transaction.yield) }}</td>
            <td class="td-cell">{{ formatYield(transaction.tradingYield) }}</td>
            <td class="td-cell">
              <div class="flex items-center gap-2">
                <span v-if="transaction.spreadBp !== null" class="flex items-center gap-1">
                  <span class="w-2 h-2 rounded-full" :class="getSpreadDotClass(transaction.spreadBp)"></span>
                  {{ formatSpread(transaction.spreadBp) }}
                </span>
                <span v-else>-</span>
              </div>
            </td>
            <td v-if="isAdmin" class="td-cell">
              <button @click="openDiscardPopup(transaction.id)" class="text-red-500 hover:text-red-700">
                <span class="px-1.5 py-0.5 text-xs bg-red-50 rounded-full hover:bg-red-100">X</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <DiscardTransactionPopup v-if="showPopup" :transactionId="selectedTransactionId" @close="handlePopupClose" />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { differenceInMonths } from 'date-fns';
import DiscardTransactionPopup from '@/components/popups/DiscardTransactionPopup.vue';
import { useStore } from 'vuex';

export default {
  name: 'TransactionInquiryResultComponent',
  components: {
    DiscardTransactionPopup,
  },
  props: {
    transactions: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const isAdmin = computed(() => store.getters['auth/isAdmin']);
    const showPopup = ref(false);
    const selectedTransactionId = ref(null);

    const openDiscardPopup = transactionId => {
      selectedTransactionId.value = transactionId;
      showPopup.value = true;
    };

    const handlePopupClose = success => {
      showPopup.value = false;
      if (success) {
        // 거래내역 갱신 로직 추가
      }
    };

    // 거래 데이터를 날짜 기준으로 역순 정렬
    const sortedTransactions = computed(() => {
      return [...props.transactions].sort((a, b) => {
        const dateA = new Date(a.transactionDateTime);
        const dateB = new Date(b.transactionDateTime);
        return dateB - dateA; // 역순 정렬
      });
    });

    // 서울 현재 시간 가져오기
    const getSeoulDate = (date = new Date()) => {
      return new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    };

    // N일 전 계산 함수
    const getDaysAgo = dateTimeString => {
      if (!dateTimeString) return 0;

      const txDate = getSeoulDate(new Date(dateTimeString));
      const seoulNow = getSeoulDate();

      // 날짜만 비교하기 위해 시간을 초기화
      seoulNow.setHours(0, 0, 0, 0);
      txDate.setHours(0, 0, 0, 0);

      const diffTime = seoulNow - txDate;
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    };

    // 날짜 포맷팅 함수
    const formatDate = dateString => {
      if (!dateString) return '-';
      const date = getSeoulDate(new Date(dateString));
      return date
        .toLocaleDateString('ko-KR', {
          timeZone: 'Asia/Seoul',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .replace(/\. /g, '-')
        .replace('.', '');
    };

    // 체결시각 포맷팅 함수
    const formatTransactionTime = dateTimeString => {
      if (!dateTimeString) return '-';

      const txDate = getSeoulDate(new Date(dateTimeString));
      const seoulNow = getSeoulDate();

      const timeFormat = {
        timeZone: 'Asia/Seoul',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };

      // 날짜 비교를 위해 시간을 제거한 복사본 생성
      const seoulToday = new Date(seoulNow);
      const txDay = new Date(txDate);
      seoulToday.setHours(0, 0, 0, 0);
      txDay.setHours(0, 0, 0, 0);

      const isToday = seoulToday.getTime() === txDay.getTime();

      if (isToday) {
        return txDate.toLocaleTimeString('ko-KR', timeFormat);
      } else {
        return txDate
          .toLocaleString('ko-KR', {
            ...timeFormat,
          })
          .replace(/\. /g, '-')
          .replace('.', '');
      }
    };

    // 금리 포맷팅 함수 (소수점 셋째자리까지)
    const formatYield = rate => {
      if (!rate) return '-';
      return `${Number(rate).toFixed(3)}%`;
    };

    // 스프레드 도트 클래스 반환 함수
    const getSpreadDotClass = spread => {
      if (spread === null || spread === undefined) return '';
      if (spread < 0) {
        return 'bg-red-500';
      } else if (spread > 0) {
        return 'bg-blue-500';
      }
      return 'bg-gray-500';
    };

    // 스프레드 포맷팅 함수 수정
    const formatSpread = spread => {
      if (spread === null || spread === undefined) return '-';

      // 소수점 첫째자리까지 고정하고 부호 추가
      const formattedNumber = Number(spread).toFixed(1);
      const sign = formattedNumber > 0 ? '+' : ''; // 음수는 자동으로 - 부호가 붙음
      return `${sign}${formattedNumber}bp`;
    };

    // 잔존만기 계산 함수 추가
    const formatRemainingMaturity = date => {
      if (!date) return '-';
      const maturityDate = new Date(date);
      const today = new Date();
      const monthsDiff = differenceInMonths(maturityDate, today);
      const years = Math.floor(monthsDiff / 12);
      const months = monthsDiff % 12;

      if (years === 0) return `${months}개월`;
      if (months === 0) return `${years}년`;
      return `${years}년 ${months}개월`;
    };

    return {
      sortedTransactions,
      formatDate,
      formatTransactionTime,
      formatYield,
      formatSpread,
      getSpreadDotClass,
      getDaysAgo,
      isAdmin,
      showPopup,
      selectedTransactionId,
      openDiscardPopup,
      handlePopupClose,
      formatRemainingMaturity,
    };
  },
};
</script>

<style scoped>
.th-cell {
  @apply px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.td-cell {
  @apply px-4 py-3 text-sm whitespace-nowrap;
}

button:active {
  transform: scale(0.98);
}
</style>
